import { html, type TemplateResult } from 'lit';
import type { RouteConfig } from '@lit-labs/router';

// Side-effect imports so the custom elements are registered before the
// router tries to render the templates that reference them. Pages are
// kept independent so they can be code-split in V2.
import './pages/deck-list-page';
import './pages/deck-viewer-page';
import './pages/deck-analytics-page';
import './pages/create-page';
import './pages/gallery-page';
import './pages/reference-page';
import './pages/typography-page';
import './pages/shared-deck-page';
import './pages/login-page';
import './pages/accept-page';
import './pages/eval-page';
import './pages/eval-decks-page';
import './pages/studio-page';
import './pages/home-page';
import './pages/library-page';
import './pages/templates-page';
import './pages/shared-page';
import './pages/images-page';

// Live presentation (Step 1) — gated behind VITE_LIVE_PRESENTATION so it ships
// dark until enabled. The dynamic imports register the custom elements.
const LIVE_PRESENTATION = import.meta.env.VITE_LIVE_PRESENTATION === '1';
if (LIVE_PRESENTATION) {
  // Presenting is in-place from the studio Present view (live-present-overlay);
  // only the anonymous viewer page has its own route.
  void import('./pages/live-viewer-page');
}

// Auth is now enforced ONCE at the shell level (<deck-app>): the gate
// resolves the Firebase auth state before any protected outlet is
// instantiated, so routes no longer wrap their pages in a per-route guard.
// `/login` and `/share/:token` are the open routes the shell always lets
// render.
export const routes: RouteConfig[] = [
  {
    // V2 Home — the building/composer landing (full design replace).
    path: '/',
    render: (): TemplateResult => html`<home-page></home-page>`,
  },
  {
    // V2 Decks library with folders.
    path: '/library',
    render: (): TemplateResult => html`<library-page></library-page>`,
  },
  {
    // V2 Templates — curated starting points.
    path: '/templates',
    render: (): TemplateResult => html`<templates-page></templates-page>`,
  },
  {
    // V2 Shared with you.
    path: '/shared',
    render: (): TemplateResult => html`<shared-page></shared-page>`,
  },
  {
    // The classic deck list (kept reachable while the library beds in).
    path: '/decks',
    render: (): TemplateResult => html`<deck-list-page></deck-list-page>`,
  },
  {
    path: '/new',
    render: (): TemplateResult => html`<create-page></create-page>`,
  },
  {
    // The V2 AI Studio — chat-first deck authoring (the new design).
    path: '/studio',
    render: (): TemplateResult => html`<studio-page></studio-page>`,
  },
  {
    // Resume an instant-saved draft. A hard refresh to /new/<id> mounts the
    // SAME create-page, which reads `deck-id` and restores the model + thread
    // (see create-page.ts step 3). /new (no id) stays the fresh-start route.
    path: '/new/:id',
    render: (params): TemplateResult =>
      html`<create-page deck-id=${params.id ?? ''}></create-page>`,
  },
  {
    // More-specific path MUST be registered before '/decks/:id' so the bare
    // viewer pattern does not swallow '/analytics'. @lit-labs/router resolves
    // the first route whose URLPattern matches, in array order.
    path: '/decks/:id/analytics',
    render: (params): TemplateResult =>
      html`<deck-analytics-page deck-id=${params.id ?? ''}></deck-analytics-page>`,
  },
  {
    path: '/decks/:id',
    render: (params): TemplateResult =>
      html`<deck-viewer-page deck-id=${params.id ?? ''}></deck-viewer-page>`,
  },
  {
    path: '/gallery',
    render: (): TemplateResult => html`<gallery-page></gallery-page>`,
  },
  {
    // V2 Images — every image the signed-in owner has generated, with soft-hide.
    path: '/images',
    render: (): TemplateResult => html`<images-page></images-page>`,
  },
  {
    path: '/reference',
    render: (): TemplateResult => html`<reference-page></reference-page>`,
  },
  {
    path: '/typography',
    render: (): TemplateResult => html`<typography-page></typography-page>`,
  },
  {
    // More-specific path MUST be registered before '/eval' so the bare eval
    // pattern does not swallow '/decks'. @lit-labs/router resolves the first
    // route whose URLPattern matches, in array order.
    path: '/eval/decks',
    render: (): TemplateResult => html`<eval-decks-page></eval-decks-page>`,
  },
  {
    path: '/eval',
    render: (): TemplateResult => html`<eval-page></eval-page>`,
  },
  {
    path: '/live/:sessionId',
    render: (params): TemplateResult =>
      LIVE_PRESENTATION
        ? html`<live-viewer-page session-id=${params.sessionId ?? ''}></live-viewer-page>`
        : html`<div style="padding:64px;text-align:center">Live presentation isn't enabled.</div>`,
  },
  {
    path: '/share/:token',
    render: (params): TemplateResult =>
      html`<shared-deck-page token=${params.token ?? ''}></shared-deck-page>`,
  },
  {
    path: '/login',
    render: (): TemplateResult => html`<login-page></login-page>`,
  },
  {
    // OPEN magic-link invite landing. The invitee is not signed in; the
    // `?token=` query param is the credential. They set a password and are
    // signed straight in. Gated open by isOpenRoute() in lib/auth-gate.ts.
    path: '/accept',
    render: (): TemplateResult => html`<accept-page></accept-page>`,
  },
];
